// PortfolioMap.jsx
// Map view showing all properties in the portfolio

import React, { useState, useEffect } from "react";
import { GoogleMap, Marker, InfoWindow } from "@react-google-maps/api";
import { useNavigate } from "react-router-dom";

const mapContainerStyle = {
  width: "100%",
  height: "600px",
  borderRadius: "12px",
};

const defaultMapOptions = {
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: true,
};

export default function PortfolioMap({ properties }) {
  const navigate = useNavigate();
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [map, setMap] = useState(null);

  // Filter properties that have geocoding data
  const mappableProperties = properties.filter((p) => p.lat && p.lng);

  // Fit bounds to show all properties
  useEffect(() => {
    if (map && mappableProperties.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      mappableProperties.forEach((property) => {
        bounds.extend({ lat: property.lat, lng: property.lng });
      });
      map.fitBounds(bounds);

      // Add padding to bounds
      const padding = { top: 50, right: 50, bottom: 50, left: 50 };
      map.fitBounds(bounds, padding);
    }
  }, [map, mappableProperties]);

  // Get marker color based on property value/equity
  const getMarkerColor = (property) => {
    const value = property.currentValue || property.valuation?.estimate || 0;

    if (value >= 1500000) return "#CC0202"; // High value - brand red
    if (value >= 1000000) return "#FF6B6B"; // Medium-high value
    if (value >= 750000) return "#FFA500"; // Medium value
    return "#4CAF50"; // Lower value - green
  };

  const handleMarkerClick = (property) => {
    setSelectedProperty(property);
  };

  const handleNavigateToProperty = (propertyId) => {
    navigate(`/property/${propertyId}`);
  };

  if (mappableProperties.length === 0) {
    return (
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-8 text-center">
        <p className="text-gray-600">
          No properties have location data yet. Add geocoding information to
          view them on the map.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl shadow-sm border border-hn-dark/5 bg-white p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-hn-dark">
          Portfolio Map
        </h2>
        <p className="text-sm text-gray-600">
          {mappableProperties.length} of {properties.length} properties mapped
        </p>
      </div>

      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        zoom={10}
        options={defaultMapOptions}
        onLoad={(map) => setMap(map)}
      >
        {mappableProperties.map((property) => (
          <Marker
            key={property.id}
            position={{ lat: property.lat, lng: property.lng }}
            title={property.name || property.address}
            onClick={() => handleMarkerClick(property)}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              fillColor: getMarkerColor(property),
              fillOpacity: 0.9,
              strokeColor: "#FFFFFF",
              strokeWeight: 2,
              scale: 12,
            }}
          />
        ))}

        {selectedProperty && (
          <InfoWindow
            position={{ lat: selectedProperty.lat, lng: selectedProperty.lng }}
            onCloseClick={() => setSelectedProperty(null)}
          >
            <div className="p-3 max-w-xs">
              <h3 className="font-bold text-lg text-hn-dark mb-2">
                {selectedProperty.name || selectedProperty.address}
              </h3>

              <div className="space-y-2">
                <p className="text-sm text-gray-600">
                  {selectedProperty.address}
                </p>

                {selectedProperty.currentValue && (
                  <div>
                    <p className="text-xs text-gray-500">Current Value</p>
                    <p className="text-lg font-bold text-hn-primary">
                      ${selectedProperty.currentValue.toLocaleString()}
                    </p>
                  </div>
                )}

                <div className="flex gap-2 text-xs text-gray-600">
                  {selectedProperty.type && (
                    <span className="bg-gray-100 px-2 py-1 rounded">
                      {selectedProperty.type}
                    </span>
                  )}
                  {selectedProperty.sqft && (
                    <span className="bg-gray-100 px-2 py-1 rounded">
                      {selectedProperty.sqft} sq ft
                    </span>
                  )}
                </div>

                <button
                  onClick={() => handleNavigateToProperty(selectedProperty.id)}
                  className="mt-3 w-full bg-hn-red text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 transition"
                >
                  View Details
                </button>
              </div>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap gap-4 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-[#4CAF50]"></div>
          <span className="text-gray-600">Under $750k</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-[#FFA500]"></div>
          <span className="text-gray-600">$750k - $1M</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-[#FF6B6B]"></div>
          <span className="text-gray-600">$1M - $1.5M</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-[#CC0202]"></div>
          <span className="text-gray-600">Over $1.5M</span>
        </div>
      </div>
    </div>
  );
}
